import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, Search, Users, ShieldCheck, GraduationCap, Sun, Moon, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    lookupMainroomParticipant,
    submitMainroomAttendance,
    type MainroomParticipantMatch,
} from "@/services/publicMainroomAttendanceService";

interface LaravelErrorResponse {
    message: string;
    errors?: Record<string, string[]>;
}

type Category = "vip" | "registered" | "other_invitee";

export default function MainroomAttendancePage() {
    const { t, i18n } = useTranslation("public");

    const parseError = (error: unknown): string => {
        if (axios.isAxiosError<LaravelErrorResponse>(error)) {
            const data = error.response?.data;
            if (data?.errors) {
                const firstKey = Object.keys(data.errors)[0];
                return data.errors[firstKey][0];
            }
            return data?.message || t("mainroom_attendance.errors.generic");
        }
        return t("mainroom_attendance.errors.unknown");
    };

    // Dark mode
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            setIsDark(true);
            document.documentElement.classList.add("dark");
        } else {
            setIsDark(false);
            document.documentElement.classList.remove("dark");
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDark;
        setIsDark(newMode);
        if (newMode) {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    };

    const toggleLanguage = () => {
        i18n.changeLanguage(i18n.language === "id" ? "en" : "id");
    };

    const [attendanceCode, setAttendanceCode] = useState("");
    const [category, setCategory] = useState<Category | null>(null);

    // Kategori VIP & Undangan Lain
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [institution, setInstitution] = useState("");

    // Kategori Peserta Terdaftar
    const [lookupEmail, setLookupEmail] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [matches, setMatches] = useState<MainroomParticipantMatch[] | null>(null);
    const [selectedRegistrationId, setSelectedRegistrationId] = useState<number | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [successResult, setSuccessResult] = useState<{ full_name: string; eligible: boolean } | null>(null);

    const handleSearch = async () => {
        if (!lookupEmail) {
            setSearchError(t("mainroom_attendance.errors.email_required"));
            return;
        }
        setIsSearching(true);
        setSearchError(null);
        setMatches(null);
        setSelectedRegistrationId(null);
        try {
            const result = await lookupMainroomParticipant(lookupEmail);
            setMatches(result);
            if (result.length === 1) {
                setSelectedRegistrationId(result[0].registration_id);
            }
        } catch (error) {
            setSearchError(parseError(error));
        } finally {
            setIsSearching(false);
        }
    };

    const isFormComplete = (() => {
        if (!attendanceCode.trim() || !category) return false;
        if (category === "registered") {
            return !!selectedRegistrationId;
        }
        return !!fullName.trim() && !!email.trim();
    })();

    const handleSubmit = async () => {
        if (!attendanceCode) {
            setSubmitError(t("mainroom_attendance.errors.code_required"));
            return;
        }
        if (!category) {
            setSubmitError(t("mainroom_attendance.errors.category_required"));
            return;
        }

        setSubmitError(null);
        setIsSubmitting(true);
        try {
            if (category === "registered") {
                if (!selectedRegistrationId) {
                    setSubmitError(t("mainroom_attendance.errors.select_registration"));
                    setIsSubmitting(false);
                    return;
                }
                const res = await submitMainroomAttendance({
                    attendance_code: attendanceCode,
                    category,
                    registration_id: selectedRegistrationId,
                });
                setSuccessResult({ full_name: res.data.full_name, eligible: res.data.eligible_for_certificate });
            } else {
                if (!fullName || !email) {
                    setSubmitError(t("mainroom_attendance.errors.name_email_required"));
                    setIsSubmitting(false);
                    return;
                }
                const res = await submitMainroomAttendance({
                    attendance_code: attendanceCode,
                    category,
                    full_name: fullName,
                    email,
                    institution: institution || undefined,
                });
                setSuccessResult({ full_name: res.data.full_name, eligible: res.data.eligible_for_certificate });
            }
        } catch (error) {
            setSubmitError(parseError(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const ThemeLangToggle = () => (
        <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <Button
                variant="ghost"
                size="icon"
                onClick={toggleLanguage}
                className="w-9 h-9 rounded-full bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                title={i18n.language === "id" ? "English" : "Bahasa Indonesia"}
            >
                <Globe className="h-4 w-4" />
            </Button>
            <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="w-9 h-9 rounded-full bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
                {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
            </Button>
        </div>
    );

    if (successResult) {
        return (
            <div className="relative min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
                <ThemeLangToggle />
                <Card className="max-w-md w-full rounded-3xl shadow-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <CardContent className="pt-10 pb-10 text-center space-y-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                            <CheckCircle2 className="w-9 h-9 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t("mainroom_attendance.success.title")}</h2>
                        <p className="text-slate-600 dark:text-slate-400">
                            {t("mainroom_attendance.success.thanks_before")}
                            <span className="font-semibold text-slate-900 dark:text-white">{successResult.full_name}</span>
                            {t("mainroom_attendance.success.thanks_after")}
                        </p>
                        {successResult.eligible ? (
                            <p className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
                                {t("mainroom_attendance.success.eligible")}
                            </p>
                        ) : (
                            <p className="text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                                {t("mainroom_attendance.success.not_eligible")}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 py-10 px-4">
            <ThemeLangToggle />
            <div className="max-w-lg mx-auto space-y-6">
                <div className="text-center space-y-2">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg shadow-indigo-600/20 p-2">
                        <img src="/konaspi.png" alt="Logo KONASPI" className="w-full h-full object-contain" />
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 dark:text-white">{t("mainroom_attendance.page_title")}</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">{t("mainroom_attendance.page_subtitle")}</p>
                </div>

                <Card className="rounded-3xl shadow-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <CardHeader>
                        <CardTitle className="text-base text-slate-900 dark:text-white">{t("mainroom_attendance.code_card.title")}</CardTitle>
                        <CardDescription className="dark:text-slate-400">{t("mainroom_attendance.code_card.desc")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Input
                            value={attendanceCode}
                            onChange={(e) => setAttendanceCode(e.target.value)}
                            placeholder={t("mainroom_attendance.code_card.placeholder")}
                            className="h-12 text-center text-lg font-bold tracking-widest uppercase rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                    </CardContent>
                </Card>

                <Card className="rounded-3xl shadow-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <CardHeader>
                        <CardTitle className="text-base text-slate-900 dark:text-white">{t("mainroom_attendance.category_card.title")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <RadioGroup value={category ?? undefined} onValueChange={(v) => setCategory(v as Category)} className="space-y-3">
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${category === "vip" ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                                <RadioGroupItem value="vip" className="mt-1" />
                                <div>
                                    <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                        <ShieldCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> {t("mainroom_attendance.category_card.vip.label")}
                                    </div>
                                    {/* <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("mainroom_attendance.category_card.vip.desc")}</p> */}
                                </div>
                            </label>
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${category === "registered" ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                                <RadioGroupItem value="registered" className="mt-1" />
                                <div>
                                    <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                        <Users className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> {t("mainroom_attendance.category_card.registered.label")}
                                    </div>
                                    {/* <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("mainroom_attendance.category_card.registered.desc")}</p> */}
                                </div>
                            </label>
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${category === "other_invitee" ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                                <RadioGroupItem value="other_invitee" className="mt-1" />
                                <div>
                                    <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                                        <GraduationCap className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> {t("mainroom_attendance.category_card.other.label")}
                                    </div>
                                    {/* <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("mainroom_attendance.category_card.other.desc")}</p> */}
                                </div>
                            </label>
                        </RadioGroup>
                    </CardContent>
                </Card>

                {category === "registered" && (
                    <Card className="rounded-3xl shadow-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                        <CardHeader>
                            <CardTitle className="text-base text-slate-900 dark:text-white">{t("mainroom_attendance.search_card.title")}</CardTitle>
                            <CardDescription className="dark:text-slate-400">{t("mainroom_attendance.search_card.desc")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex gap-2">
                                <Input
                                    type="email"
                                    value={lookupEmail}
                                    onChange={(e) => setLookupEmail(e.target.value)}
                                    placeholder={t("mainroom_attendance.search_card.placeholder")}
                                    className="h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                                />
                                <Button onClick={handleSearch} disabled={isSearching} className="h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700">
                                    {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                </Button>
                            </div>
                            {searchError && <p className="text-sm text-red-600 dark:text-red-400">{searchError}</p>}
                            {matches && matches.length > 0 && (
                                <div className="space-y-2">
                                    {matches.map((m) => (
                                        <label
                                            key={m.registration_id}
                                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${selectedRegistrationId === m.registration_id ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700"}`}
                                        >
                                            <input
                                                type="radio"
                                                name="registration_match"
                                                checked={selectedRegistrationId === m.registration_id}
                                                onChange={() => setSelectedRegistrationId(m.registration_id)}
                                            />
                                            <div>
                                                <p className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{m.participant_name}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {m.institution_name} · {m.is_presenter ? t("mainroom_attendance.search_card.presenter") : t("mainroom_attendance.search_card.participant")} · {m.conference_name}
                                                </p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {(category === "vip" || category === "other_invitee") && (
                    <Card className="rounded-3xl shadow-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                        <CardHeader>
                            <CardTitle className="text-base text-slate-900 dark:text-white">{t("mainroom_attendance.manual_card.title")}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-1.5">
                                <Label className="dark:text-slate-300">{t("mainroom_attendance.manual_card.full_name")}</Label>
                                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} className="h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white" placeholder={t("mainroom_attendance.manual_card.full_name_placeholder")} />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="dark:text-slate-300">{t("mainroom_attendance.manual_card.email")}</Label>
                                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white" placeholder={t("mainroom_attendance.manual_card.email_placeholder")} />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="dark:text-slate-300">{t("mainroom_attendance.manual_card.institution")}</Label>
                                <Input value={institution} onChange={(e) => setInstitution(e.target.value)} className="h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white" placeholder={t("mainroom_attendance.manual_card.institution_placeholder")} />
                            </div>
                        </CardContent>
                    </Card>
                )}

                {submitError && (
                    <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-3">{submitError}</div>
                )}

                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormComplete}
                    className="w-full h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-base font-bold shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                    {t("mainroom_attendance.submit_button")}
                </Button>
            </div>
        </div>
    );
}
